import { Drawer, Card, Typography, Tag, Space, Empty } from 'antd';
import {
  InfoCircleOutlined,
  CloudOutlined,
  ToolOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { getAllWidgets } from './widgets/widgetRegistry';
import { useDashboard } from './useDashboard';
import type { WidgetCategory, WidgetDefinition } from './widgets/types';

const { Text, Title } = Typography;

const CATEGORY_LABELS: Record<WidgetCategory, { label: string; color: string; icon: React.ReactNode }> = {
  weather: { label: 'Weather', color: 'blue', icon: <CloudOutlined /> },
  map: { label: 'Map', color: 'green', icon: <EnvironmentOutlined /> },
  ops: { label: 'Operations', color: 'orange', icon: <ToolOutlined /> },
  info: { label: 'Information', color: 'purple', icon: <InfoCircleOutlined /> },
};

interface AddWidgetDrawerProps {
  open: boolean;
  onClose: () => void;
}

let widgetCounter = 0;

export const AddWidgetDrawer = ({ open, onClose }: AddWidgetDrawerProps) => {
  const { addWidget } = useDashboard();
  const allWidgets = getAllWidgets();

  const grouped = allWidgets.reduce(
    (acc, w) => {
      if (!acc[w.category]) acc[w.category] = [];
      acc[w.category].push(w);
      return acc;
    },
    {} as Record<WidgetCategory, WidgetDefinition[]>,
  );

  const handleAdd = (def: WidgetDefinition) => {
    widgetCounter += 1;
    const id = `w-${Date.now()}-${widgetCounter}`;

    addWidget(
      {
        id,
        type: def.type,
        title: def.title,
        config: {},
      },
      {
        width: def.defaultSize.width,
        height: def.defaultSize.height,
      },
    );
    onClose();
  };

  return (
    <Drawer
      title="Add Widget"
      placement="right"
      open={open}
      onClose={onClose}
      size={360}
    >
      {allWidgets.length === 0 ? (
        <Empty description="No widgets available" />
      ) : (
        <Space orientation="vertical" style={{ width: '100%' }} size={16}>
          {(['weather', 'ops', 'info', 'map'] as WidgetCategory[]).filter((c) => grouped[c]?.length).map((category) => {
            const cat = CATEGORY_LABELS[category];
            return (
              <div key={category}>
                <div style={{ marginBottom: 8 }}>
                  <Tag color={cat.color} icon={cat.icon}>
                    {cat.label}
                  </Tag>
                </div>
                <Space orientation="vertical" style={{ width: '100%' }} size={8}>
                  {grouped[category].map((def) => (
                    <Card
                      key={def.type}
                      size="small"
                      hoverable
                      onClick={() => handleAdd(def)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Title level={5} style={{ margin: 0, fontSize: 14 }}>
                        {def.title}
                      </Title>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {def.description}
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Default: {def.defaultSize.width}x{def.defaultSize.height}
                        </Text>
                      </div>
                    </Card>
                  ))}
                </Space>
              </div>
            );
          })}
        </Space>
      )}
    </Drawer>
  );
};
